import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, MessageSquare, Pencil, Trash2 } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { classNames } from '../../utils/classNames';
import { Tooltip } from '../../components/ui/Tooltip';

export const TaskSwitcher = ({
  chats = [],
  activeChatId,
  disabled = false,
  onSelect,
  onEdit,
  onDelete
}) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const activeChat = chats.find((chat) => chat.id === activeChatId) || chats[0] || null;
  const activeTitle = activeChat?.title || t('defaultTaskTitle');

  useEffect(() => {
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const handleSelect = (chatId) => {
    onSelect?.(chatId);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative min-w-0 flex-1">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        disabled={disabled || !activeChat}
        className="flex w-full min-w-0 max-w-full items-center gap-1.5 rounded-lg px-1.5 py-1 text-start transition hover:bg-neutral-100 disabled:opacity-50 dark:hover:bg-accent-soft"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-neutral-100 text-neutral-500 dark:bg-accent-soft dark:text-accent">
          <MessageSquare size={13} />
        </span>
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-neutral-950 dark:text-text">
          {activeTitle}
        </span>
        <ChevronDown
          size={14}
          className={classNames('shrink-0 text-neutral-400 transition', open && 'rotate-180')}
        />
      </button>

      {open ? (
        <div className="absolute start-0 z-50 mt-1.5 w-[min(100%,20rem)] overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-[0_16px_40px_rgba(15,23,42,0.12)] dark:border-border dark:bg-panel dark:shadow-[0_16px_40px_rgba(0,0,0,0.4)]">
          <div className="border-b border-neutral-100 px-3 py-2 dark:border-border">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-400">
              {t('tasksList')}
            </p>
          </div>
          <ul className="max-h-72 overflow-y-auto p-1.5" role="listbox">
            {chats.map((chat) => {
              const isActive = chat.id === activeChatId;
              const title = chat.title || t('defaultTaskTitle');

              return (
                <li key={chat.id} role="option" aria-selected={isActive}>
                  <div
                    className={classNames(
                      'group flex items-center gap-1 rounded-xl px-1 py-1 transition',
                      isActive
                        ? 'bg-neutral-100 dark:bg-accent-soft'
                        : 'hover:bg-neutral-50 dark:hover:bg-white/[0.04]'
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => handleSelect(chat.id)}
                      className="flex min-w-0 flex-1 items-center gap-2 rounded-lg px-1.5 py-1.5 text-start"
                    >
                      <span
                        className={classNames(
                          'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg',
                          isActive
                            ? 'bg-white text-neutral-900 shadow-sm dark:bg-panel-strong dark:text-text'
                            : 'bg-neutral-100 text-neutral-500 dark:bg-white/[0.06] dark:text-muted'
                        )}
                      >
                        {isActive ? <Check size={13} /> : <MessageSquare size={13} />}
                      </span>
                      <span
                        className={classNames(
                          'min-w-0 truncate text-sm',
                          isActive
                            ? 'font-semibold text-neutral-950 dark:text-text'
                            : 'text-neutral-700 dark:text-muted'
                        )}
                      >
                        {title}
                      </span>
                    </button>
                    <div className="flex shrink-0 items-center gap-0.5 pe-1">
                      <Tooltip content={t('editChat')} position="bottom">
                        <button
                          type="button"
                          onClick={() => {
                            setOpen(false);
                            onEdit?.(chat);
                          }}
                          aria-label={t('editChat')}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-neutral-400 transition hover:bg-white hover:text-neutral-900 dark:hover:bg-white/[0.06] dark:hover:text-text"
                        >
                          <Pencil size={13} />
                        </button>
                      </Tooltip>
                      <Tooltip content={t('deleteChat')} position="bottom">
                        <button
                          type="button"
                          onClick={() => {
                            setOpen(false);
                            onDelete?.(chat);
                          }}
                          aria-label={t('deleteChat')}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-neutral-400 transition hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/40"
                        >
                          <Trash2 size={13} />
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
};
